/* Hero dress-up (cores) — LATITID
 * Each model wears her own bikini. The swatches under a model change only that model; the
 * "Vestir todas" swatches dress every model the same. On desktop the round cursor (left/right half)
 * passes every bikini to the neighbouring model; on touch a horizontal swipe does the same.
 * Changes fade in per model and are queued, so quick clicks never fight each other.
 */
(() => {
  if (customElements.get('hero-dress-up-picker')) return;

  const settle = (a) => (a && a.finished ? a.finished.catch(() => {}) : Promise.resolve());

  class HeroDressUpPicker extends HTMLElement {
    connectedCallback() {
      this.models = [...this.querySelectorAll('[data-model]')];
      this.products = [...this.querySelectorAll('[data-product]')];
      this.n = Math.min(this.models.length, this.products.length);
      if (!this.n) return;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.live = this.querySelector('[data-live]');
      this.labelModel = this.dataset.labelModel || 'Modelo';
      this.chain = Promise.resolve();
      this.pending = 0;

      this.models.forEach((model) => {
        model.frame = model.querySelector('[data-frame]');
        model.dressed = [...model.querySelectorAll('[data-dressed]')];
        model.motions = {};
        model.querySelectorAll('[data-motion]').forEach((v) => { model.motions[v.dataset.motion] = v; });
        model.play = model.querySelector('[data-play]');
      });
      this.worn = this.models.map((model, m) => {
        const start = parseInt(model.dataset.start, 10);
        return Number.isInteger(start) && start >= 0 && start < this.n ? start : m % this.n;
      });
      this.models.forEach((model, m) => {
        model.dressed.forEach((img, p) => {
          img.loading = 'eager';
          img.style.opacity = p === this.worn[m] ? 1 : 0;
        });
      });

      this.bindSwatches();
      this.bindPlay();
      this.bindRotate();
      this.bindCursor();
      this.onResize = () => this.sizeToViewport();
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onResize, { once: true });
      requestAnimationFrame(() => requestAnimationFrame(this.onResize));
      setTimeout(this.onResize, 600);
      this.render(false);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    wrap(i) { return ((i % this.n) + this.n) % this.n; }

    /* ---------- actions ---------- */
    bindSwatches() {
      this.models.forEach((model, m) => {
        model.querySelectorAll('[data-swatch]').forEach((btn) => {
          btn.addEventListener('click', () => this.pick(m, parseInt(btn.dataset.swatch, 10)));
        });
      });
      this.querySelectorAll('[data-swatch-all]').forEach((btn) => {
        btn.addEventListener('click', () => this.dressAll(parseInt(btn.dataset.swatchAll, 10)));
      });
    }

    /* ---------- play button: plays the video of the bikini the model wears now ----------
     * The video sits over the still, fades in once it is really playing, plays once and fades
     * back to the still when it ends (she finishes in her own pose). Pressing again stops it. */
    bindPlay() {
      this.models.forEach((model, m) => {
        if (!model.play) return;
        model.play.addEventListener('click', (e) => {
          e.stopPropagation();
          if (model.active) this.stopVideo(m);
          else this.playVideo(m);
        });
        Object.values(model.motions).forEach((video) => {
          video.muted = true;
          video.addEventListener('playing', () => { if (video === model.active) video.classList.add('is-playing'); });
          // Plays once and stays paused on the last frame (she ends in her own pose); no swap back.
          video.addEventListener('ended', () => { if (video === model.active) this.endVideo(m); });
        });
      });
    }

    playVideo(m) {
      const model = this.models[m];
      const video = model.motions[this.worn[m]];
      if (!video) return;
      model.active = video;
      model.play.classList.add('is-active');
      model.play.setAttribute('aria-pressed', 'true');
      video.preload = 'auto';
      try { video.currentTime = 0; } catch (err) { /* not seekable yet */ }
      const started = video.play();
      if (started && started.catch) started.catch(() => this.stopVideo(m));
    }

    /* natural end: keep the last frame on screen */
    endVideo(m) {
      const model = this.models[m];
      const video = model.active;
      model.active = null;
      if (model.play) {
        model.play.classList.remove('is-active');
        model.play.setAttribute('aria-pressed', 'false');
      }
      if (!video) return;
      video.pause();
      // drop the "playing" layer state; the paused frame stays visible only while it is the still
      video.classList.remove('is-playing');
    }

    /* stop pressed (or bikini changed): back to the first frame, which is the still */
    stopVideo(m) {
      const model = this.models[m];
      const video = model.active;
      this.endVideo(m);
      if (!video) return;
      try { video.currentTime = 0; } catch (err) { /* noop */ }
    }

    /* The paused video is the still for combos that have one (first frame), so pressing play never
     * changes colour or light. The image underneath only shows until the video has loaded.
     * The play button only shows when the bikini she wears has a video. */
    renderPlay() {
      this.models.forEach((model, m) => {
        if (!model.play) return;
        const current = model.motions[this.worn[m]];
        model.play.hidden = !current;
        if (model.active && model.active !== current) this.stopVideo(m);
        Object.values(model.motions).forEach((video) => {
          const on = video === current;
          if (on && !video.classList.contains('is-still')) {
            video.preload = 'auto';
            if (video.readyState === 0) video.load();
            if (!model.active) { try { video.currentTime = 0; } catch (err) { /* not seekable yet */ } }
          }
          video.classList.toggle('is-still', on);
        });
      });
    }

    pick(m, p) { this.enqueue(() => this.worn.map((q, i) => (i === m ? p : q)), 1); }

    dressAll(p) { this.enqueue(() => this.worn.map(() => p), 1); }

    /* Every bikini moves to the next model in the direction of travel */
    rotate(dir) { this.enqueue(() => this.worn.map((_, i) => this.worn[this.wrap(i - dir)]), dir); }

    enqueue(makeTarget, dir) {
      // Keep at most one step waiting so fast clicking never builds a long backlog
      if (this.pending > 1) return;
      this.pending += 1;
      this.chain = this.chain
        .then(() => this.apply(makeTarget(), dir))
        .catch(() => {})
        .finally(() => { this.pending -= 1; });
    }

    async apply(target, dir) {
      const changed = target.map((p, m) => (p !== this.worn[m] ? m : -1)).filter((m) => m >= 0);
      if (!changed.length) return;
      this.classList.add('is-busy');
      const ordered = dir < 0 ? [...changed].reverse() : changed;
      const anims = ordered.map((m, k) => {
        const img = this.models[m].dressed[target[m]];
        img.style.zIndex = 2;
        return img.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: this.reduced.matches ? 250 : 560,
          delay: this.reduced.matches ? 0 : k * 70,
          easing: 'ease-in-out',
          fill: 'both',
        });
      });
      // swatches react immediately
      this.worn = target;
      this.render(true);
      await Promise.all(anims.map(settle));
      this.models.forEach((model, m) => model.dressed.forEach((img, p) => {
        img.style.opacity = p === this.worn[m] ? 1 : 0;
        img.style.zIndex = '';
      }));
      anims.forEach((a) => a.cancel());
      this.classList.remove('is-busy');
    }

    render(announce) {
      const names = [];
      this.models.forEach((model, m) => {
        const product = this.products[this.worn[m]];
        if (!product) return;
        const title = product.dataset.title || '';
        names.push(`${this.labelModel} ${m + 1}: ${title}`);
        const link = model.querySelector('[data-caption-link]');
        const titleEl = model.querySelector('[data-caption-title]');
        const priceEl = model.querySelector('[data-caption-price]');
        if (titleEl) titleEl.textContent = title;
        if (priceEl) priceEl.textContent = product.dataset.price || '';
        if (link) {
          if (product.dataset.url) link.setAttribute('href', product.dataset.url);
          else link.removeAttribute('href');
        }
        model.querySelectorAll('[data-swatch]').forEach((btn) => {
          const on = parseInt(btn.dataset.swatch, 10) === this.worn[m];
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      });
      this.renderPlay();
      const same = this.worn.every((p) => p === this.worn[0]);
      this.querySelectorAll('[data-swatch-all]').forEach((btn) => {
        const on = same && parseInt(btn.dataset.swatchAll, 10) === this.worn[0];
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (announce && this.live) this.live.textContent = names.join('; ');
    }

    /* ---------- rotation: keyboard + swipe ---------- */
    bindRotate() {
      if (this.dataset.rotate !== 'true' || this.n < 2) return;
      this.addEventListener('keydown', (e) => {
        if (e.target.closest('[data-swatch], [data-swatch-all]')) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.rotate(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.rotate(1); }
      });
      let start = null;
      this.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        start = e.touches.length === 1 && !e.target.closest('button, a') ? { x: t.clientX, y: t.clientY, t: performance.now() } : null;
      }, { passive: true });
      this.addEventListener('touchend', (e) => {
        const t = e.changedTouches && e.changedTouches[0];
        if (!start || !t) return;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        const quick = performance.now() - start.t < 800;
        start = null;
        if (quick && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) this.rotate(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    /* ---------- desktop cursor bubble (same look as Hero dress-up) ---------- */
    bindCursor() {
      const cursor = this.querySelector('[data-cursor]');
      if (!cursor || this.n < 2) return;
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      const isUi = (target) => target instanceof Element && target.closest('a[href], button, input, select, textarea, [data-no-cursor], .hd__swatches, .hd__all');
      let tx = 0;
      let ty = 0;
      let x = 0;
      let y = 0;
      let raf = null;
      let shown = false;
      let side = 1;
      const place = () => { cursor.style.translate = `${x}px ${y}px`; };
      const loop = () => {
        x += (tx - x) * 0.25;
        y += (ty - y) * 0.25;
        place();
        raf = Math.abs(tx - x) > 0.2 || Math.abs(ty - y) > 0.2 ? requestAnimationFrame(loop) : null;
      };
      this.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse' || !fine.matches) return;
        const r = this.getBoundingClientRect();
        tx = e.clientX - r.left;
        ty = e.clientY - r.top;
        side = tx < r.width / 2 ? -1 : 1;
        cursor.classList.toggle('is-prev', side < 0);
        const ui = !!isUi(e.target);
        cursor.classList.toggle('is-on', !ui);
        this.classList.toggle('has-cursor', !ui);
        if (!shown || this.reduced.matches) {
          x = tx;
          y = ty;
          place();
          shown = true;
        } else if (!raf) {
          raf = requestAnimationFrame(loop);
        }
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        shown = false;
        cursor.classList.remove('is-on');
        this.classList.remove('has-cursor');
      });
      this.addEventListener('click', (e) => {
        if (!fine.matches || (e.pointerType && e.pointerType !== 'mouse') || isUi(e.target)) return;
        if (!this.reduced.matches) cursor.animate([{ scale: 0.82 }, { scale: 1 }], { duration: 260, easing: 'cubic-bezier(.2,.8,.2,1)' });
        this.rotate(side);
      });
    }

    /* Fit the visible viewport and keep content below overlay headers (same as Hero dress-up) */
    sizeToViewport() {
      const top = this.getBoundingClientRect().top + window.scrollY;
      const vh = window.innerHeight;
      const offset = top < vh * 0.5 ? top : 0;
      this.style.setProperty('--hd-h', `${Math.round(vh - offset)}px`);
      let overlap = 0;
      const me = this.getBoundingClientRect();
      document.querySelectorAll('sticky-header, .header-wrapper, header').forEach((header) => {
        if (header.contains(this)) return;
        const hr = header.getBoundingClientRect();
        if (hr.height && hr.top <= me.top + 1 && hr.bottom > me.top) overlap = Math.max(overlap, hr.bottom - me.top);
      });
      this.style.setProperty('--hd-overlap', `${Math.round(overlap)}px`);
    }
  }

  customElements.define('hero-dress-up-picker', HeroDressUpPicker);
})();
